/**
 * ══════════════════════════════════════════════════════════════
 * InteractiveChart — Componente padrão para gráficos interativos
 * ══════════════════════════════════════════════════════════════
 *
 * Centraliza padrões de:
 * - Drill-down via clique em elemento
 * - Tooltip padronizado (CHART_TOOLTIP_STYLE)
 * - Filtro toggle com indicadores visuais
 * - Cursor pointer automático
 * - Hint de interação ("Clique para...")
 * - Estado vazio padronizado
 *
 * Uso obrigatório para todos os gráficos interativos do Netuno.
 *
 * @example
 * // PieChart com filtro toggle
 * <InteractiveChart
 *   data={distributionData}
 *   hint="Clique em uma fatia para filtrar o ranking"
 *   activeFilter={critFilter}
 *   onFilterChange={(e) => setCritFilter(e.filterKey)}
 *   onClearFilter={() => setCritFilter(null)}
 *   height={180}
 * >
 *   {(chartProps) => (
 *     <PieChart>
 *       <Pie
 *         data={chartProps.data}
 *         onClick={(_, idx) => chartProps.onElementClick?.({ type: 'click', data: chartProps.data[idx], index: idx, chartType: 'pie' })}
 *         className="cursor-pointer"
 *       >
 *         {chartProps.data.map((entry, idx) => (
 *           <Cell key={idx} fill={entry.fill} {...chartProps.getCellProps(entry.key)} />
 *         ))}
 *       </Pie>
 *       <Tooltip {...chartProps.tooltipProps} />
 *     </PieChart>
 *   )}
 * </InteractiveChart>
 *
 * @example
 * // LineChart com drill-down
 * <InteractiveChart
 *   data={chartData}
 *   hint="Clique em um ponto para analisar o contrato"
 *   onDrillDown={(e) => navigate(e.targetRoute)}
 *   height={320}
 * >
 *   {(chartProps) => (
 *     <LineChart data={chartData}>
 *       <Tooltip {...chartProps.tooltipProps} />
 *       <Line activeDot={{ onClick: () => chartProps.onDrillDown?.({ data: point, targetRoute: `/contrato/${id}` }) }} />
 *     </LineChart>
 *   )}
 * </InteractiveChart>
 * ══════════════════════════════════════════════════════════════
 */

import React from 'react';
import { ResponsiveContainer } from 'recharts';
import { CHART_TOOLTIP_STYLE } from './ChartTooltip';
import type {
  ChartInteractionEvent,
  ChartFilterChangeEvent,
  ChartDrillDownEvent,
  ChartDataPoint,
} from '@/types/charts';

// ─── Types ──────────────────────────────────────────────────
export interface InteractiveChartProps<T = any> {
  /** Data array for the chart */
  data: T[];
  /** Height of the chart container in px */
  height?: number;
  /** Hint text shown above chart (e.g., "Clique em uma fatia para filtrar") */
  hint?: string;
  /** Message shown when data is empty */
  emptyMessage?: string;
  /** Currently active filter key (enables toggle visual) */
  activeFilter?: string | null;
  /** Callback to clear filter */
  onClearFilter?: () => void;
  /** Callback when a chart element is clicked */
  onElementClick?: (event: ChartInteractionEvent<T>) => void;
  /** Callback when filter state changes via chart interaction */
  onFilterChange?: (event: ChartFilterChangeEvent) => void;
  /** Callback for drill-down navigation from chart */
  onDrillDown?: (event: ChartDrillDownEvent<T>) => void;
  /** Filter label for the clear button */
  activeFilterLabel?: string;
  /** Additional className for the container */
  className?: string;
  /** Render function receiving chart utilities */
  children: (chartProps: InteractiveChartRenderProps<T>) => React.ReactNode;
}

export interface InteractiveChartRenderProps<T = any> {
  /** Original data */
  data: T[];
  /** Pre-configured tooltip props (contentStyle) */
  tooltipProps: {
    contentStyle: React.CSSProperties;
  };
  /** Returns opacity and className props for Cell elements based on active filter */
  getCellProps: (itemKey: string) => {
    opacity: number;
    className: string;
  };
  /** Active filter value */
  activeFilter: string | null;
  /** Typed element click handler — pass to Recharts onClick */
  onElementClick: ((event: ChartInteractionEvent<T>) => void) | undefined;
  /** Typed filter change handler */
  onFilterChange: ((event: ChartFilterChangeEvent) => void) | undefined;
  /** Typed drill-down handler */
  onDrillDown: ((event: ChartDrillDownEvent<T>) => void) | undefined;
}

// ─── Component ──────────────────────────────────────────────
export function InteractiveChart<T = any>({
  data,
  height = 200,
  hint,
  emptyMessage = 'Sem dados para exibir',
  activeFilter = null,
  onClearFilter,
  onElementClick,
  onFilterChange,
  onDrillDown,
  activeFilterLabel,
  className,
  children,
}: InteractiveChartProps<T>) {
  const handleClearFilter = () => {
    const previousKey = activeFilter;
    onClearFilter?.();
    onFilterChange?.({ filterKey: null, previousKey, source: 'clear-button' });
  };

  const chartProps: InteractiveChartRenderProps<T> = {
    data,
    tooltipProps: {
      contentStyle: CHART_TOOLTIP_STYLE,
    },
    getCellProps: (itemKey: string) => ({
      opacity: activeFilter && activeFilter !== itemKey ? 0.3 : 1,
      className: 'cursor-pointer transition-opacity',
    }),
    activeFilter,
    onElementClick,
    onFilterChange,
    onDrillDown,
  };

  return (
    <div className={className}>
      {/* Hint + Filter clear */}
      <div className="flex items-center justify-between mb-2">
        {hint && (
          <p className="text-xs text-muted-foreground/70 italic">{hint}</p>
        )}
        {activeFilter && onClearFilter && (
          <button
            onClick={handleClearFilter}
            className="text-xs text-primary hover:underline"
          >
            ✕ {activeFilterLabel || 'Limpar filtro'}
          </button>
        )}
      </div>

      {/* Empty state */}
      {data.length === 0 ? (
        <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>
          {emptyMessage}
        </div>
      ) : (
        <div style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            {children(chartProps) as React.ReactElement}
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default InteractiveChart;
